import { ListChecks, MessageSquare, Settings, Users, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import type { SidePanelTab } from "./MeetingControls";
import { useCallLayoutTier } from "../hooks/useCallLayoutTier";

interface SidePanelProps {
  /** Active tab; null means panel is closed. */
  activeTab: SidePanelTab;
  onClose: () => void;
  /** Tab content slots; rendered based on activeTab. */
  chatContent?: React.ReactNode;
  usersContent?: React.ReactNode;
  inviteContent?: React.ReactNode;
  /** US 12.2 — the meeting's Action items. */
  tasksContent?: React.ReactNode;
  /** Shown as "{n} in call" in the People header, matching the design's own
   *  People panel exactly (`{all.length} in call`) — omitted for other tabs. */
  participantCount?: number;
}

const icons: Record<NonNullable<SidePanelTab>, React.ReactNode> = {
  chat: <MessageSquare className="size-4 text-primary" />,
  // The design colors the Chat header icon `text-primary` but the People
  // header icon `text-foreground` (weco2/call.jsx).
  users: <Users className="size-4 text-foreground" />,
  invite: <Settings className="size-4 text-primary" />,
  tasks: <ListChecks className="size-4 text-primary" />,
};

/**
 * In-call side panels, shaped like the design's own two (weco2/call.jsx):
 * People is a floating card (`top-3 right-3 bottom-24`, `rounded-[28px]`),
 * Chat is an edge drawer (`top-0 right-0 bottom-20`, `border-l`). Both are
 * `w-80 max-w-[85vw]`. Phones keep an inset sheet above the bar.
 */
export function SidePanel({
  activeTab,
  onClose,
  chatContent,
  usersContent,
  inviteContent,
  tasksContent,
  participantCount,
}: SidePanelProps) {
  const { t } = useTranslation();
  const isMobile = useCallLayoutTier() === "mobile";
  const isUsers = activeTab === "users";
  const panelClass = isMobile
    ? "absolute inset-x-3 top-3 bottom-24 rounded-3xl bg-background border border-border flex flex-col overflow-hidden z-30 shadow-2xl"
    : isUsers
      ? "absolute top-3 end-3 bottom-24 w-80 max-w-[85vw] rounded-[28px] bg-background border border-border flex flex-col overflow-hidden z-30 shadow-2xl"
      : "absolute top-0 end-0 bottom-20 w-80 max-w-[85vw] bg-background border-s border-border flex flex-col overflow-hidden z-30 shadow-2xl";
  const title =
    activeTab === "users"
      ? t("call.controls.people")
      : activeTab === "invite"
        ? t("call.controls.invitePeople")
        : activeTab === "tasks"
          ? t("tasks.meeting.title", { defaultValue: "Action items" })
          : t("call.controls.inCallChat", { defaultValue: "In-call chat" });
  return (
    <AnimatePresence>
      {activeTab !== null && (
        <motion.aside
          key="side-panel"
          initial={{ y: isMobile ? 24 : 0, x: isMobile ? 0 : "100%", opacity: isMobile ? 0 : 1 }}
          animate={{ y: 0, x: 0, opacity: 1 }}
          exit={{ y: isMobile ? 24 : 0, x: isMobile ? 0 : "100%", opacity: isMobile ? 0 : 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={panelClass}
        >
          {isUsers ? (
            <header className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
              {icons.users}
              <h2 className="text-foreground text-xs font-black uppercase tracking-widest truncate">{title}</h2>
              {participantCount !== undefined && (
                <span className="ms-auto text-muted-foreground text-xs font-bold shrink-0">
                  {t("call.controls.inCall", { count: participantCount })}
                </span>
              )}
              <CloseButton onClose={onClose} className={participantCount === undefined ? "ms-auto" : ""} />
            </header>
          ) : (
            <header className="h-14 flex items-center justify-between gap-2 px-4 border-b border-border shrink-0">
              <h2 className="font-black text-sm flex items-center gap-2 text-foreground truncate">
                {icons[activeTab]} {title}
              </h2>
              <CloseButton onClose={onClose} />
            </header>
          )}
          {/* Chat and People manage their own scrolling so their composer /
              search stays pinned, as in the design. */}
          <div
            className={
              activeTab === "invite" || activeTab === "tasks"
                ? "flex-1 min-h-0 overflow-y-auto no-scrollbar"
                : "flex-1 min-h-0 flex flex-col overflow-hidden"
            }
          >
            {activeTab === "chat" && chatContent}
            {activeTab === "users" && usersContent}
            {activeTab === "invite" && inviteContent}
            {activeTab === "tasks" && tasksContent}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function CloseButton({ onClose, className = "" }: { onClose: () => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t("call.controls.closePanel", { defaultValue: "Close panel" })}
      className={`size-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground shrink-0 transition-colors ${className}`}
    >
      <X className="size-4" />
    </button>
  );
}
